import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export const generateAuditReport = async (
    auditData: any,
    patientName: string = "Patient",
    showMedicalAuditAIRecommendations: boolean = false
) => {
    if (!auditData) return;

    console.log("generateAuditReport called with:", auditData);
    console.log("Patient name passed:", patientName);

    const uploadedFilename = auditData.metadata.file_url || "Unknown File";
    console.log("Extracted filename:", uploadedFilename);

    // Create a temporary container for the report content
    const reportContainer = document.createElement("div");
    reportContainer.style.position = "absolute";
    reportContainer.style.left = "0px";
    reportContainer.style.top = "-10000px";
    reportContainer.style.width = "1200px";
    reportContainer.style.backgroundColor = "#FFFFFF";
    reportContainer.style.color = "#121212";
    reportContainer.style.padding = "40px";
    reportContainer.style.fontFamily = "Arial, sans-serif";

    // Build the report HTML
    const reportHTML = generateReportHTML(
        auditData,
        patientName,
        uploadedFilename,
        showMedicalAuditAIRecommendations
    );
    console.log("Report HTML generated, length:", reportHTML.length);
    reportContainer.innerHTML = reportHTML;
    document.body.appendChild(reportContainer);

    console.log("Report container added to DOM, children:", reportContainer.childNodes.length);

    try {
        // Wait a bit for content to render
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Convert HTML to canvas
        const canvas = await html2canvas(reportContainer, {
            scale: 2,
            backgroundColor: "#FFFFFF",
            useCORS: true,
            logging: true,
        });

        console.log("Canvas created, size:", canvas.width, "x", canvas.height);

        // Create PDF
        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
        });

        const imgData = canvas.toDataURL("image/png");
        const pageHeight = pdf.internal.pageSize.getHeight();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * pageWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        // Add image to PDF (handles multiple pages)
        pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        // Generate filename
        const date = new Date().toISOString().split("T")[0];
        const filenameBase = uploadedFilename.replace(/\.pdf$/i, "");
        const filename = `${filenameBase}_Audit_Report.pdf`;

        console.log("PDF created, saving as:", filename);

        // Download PDF
        pdf.save(filename);
    } finally {
        // Clean up
        document.body.removeChild(reportContainer);
    }
};

export const generateReportHTML = (
    auditData: any,
    patientName: string,
    uploadedFilename: string,
    showMedicalAuditAIRecommendations: boolean = false
  ): string => {
  console.log("%c Line:95 🍤 auditData", "color:#4fff4B", auditData);
    const categories = auditData.categories || [];

    // Calculate overall compliance from category compliance percentages
    const overallCompliance =
        categories.length > 0
            ? Math.round(
                categories.reduce(
                    (sum: number, cat: any) => sum + (cat.compliance_percent || 0),
                    0
                ) / categories.length
            )
            : 0;

    // Count categories by status
    const statusBreakdown = categories.reduce((acc: any, cat: any) => {
        const status = cat.status?.toLowerCase() || "unknown";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    const now = new Date();
    const timestamp = now.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });

    return `
    <div style="font-family: 'Arial', sans-serif; color: #121212; line-height: 1.6;">
      <!-- Header -->
      <div style="border-bottom: 4px solid #00B8D9; padding-bottom: 20px; margin-bottom: 30px;">
        <h1 style="margin: 0; font-size: 36px; font-weight: bold; color: #00B8D9;">
          Medical Note Audit Report
        </h1>
      </div>

      <!-- Executive Summary -->
      <div style="margin-bottom: 30px; background: #f9f9f9; padding: 20px; border-radius: 8px;">
        <h2 style="margin: 0 0 12px 0; font-size: 18px; font-weight: bold;">
          Executive Summary
        </h2>
        
        <!-- Metadata Grid -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 15px;">
          <!-- Left Column -->
          <div>
            <div style="margin-bottom: 12px;">
              <div style="font-size: 11px; color: #999; font-weight: bold; margin-bottom: 4px;">AUDIT ID</div>
              <div style="font-size: 13px; color: #333;">${auditData.audit_id || "N/A"}</div>
            </div>
            <div style="margin-bottom: 12px;">
              <div style="font-size: 11px; color: #999; font-weight: bold; margin-bottom: 4px;">PATIENT</div>
              <div style="font-size: 13px; color: #333;">${patientName}</div>
            </div>
            <div>
              <div style="font-size: 11px; color: #999; font-weight: bold; margin-bottom: 4px;">FILE</div>
              <div style="font-size: 13px; color: #333;">${uploadedFilename}</div>
            </div>
          </div>

          <!-- Right Column -->
          <div>
            <div style="margin-bottom: 12px;">
              <div style="font-size: 11px; color: #999; font-weight: bold; margin-bottom: 4px;">PRODUCT</div>
              <div style="font-size: 13px; color: #333;">${auditData.metadata.product_name || "N/A"}</div>
            </div>
            <div style="margin-bottom: 12px;">
              <div style="font-size: 11px; color: #999; font-weight: bold; margin-bottom: 4px;">MAC JURISDICTION</div>
              <div style="font-size: 13px; color: #333;">${auditData.metadata.mac_jurisdiction || "N/A"}</div>
            </div>
            <div>
              <div style="font-size: 11px; color: #999; font-weight: bold; margin-bottom: 4px;">APPLIED STANDARDS</div>
              <div style="font-size: 13px; color: #333;">
                ${auditData.metadata.applied_standards && auditData.metadata.applied_standards.length > 0
            ? auditData.metadata.applied_standards.join(", ")
            : "N/A"
        }
              </div>
            </div>
          </div>
        </div>

        <!-- Overall Compliance Score -->
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #ddd;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; align-items: center;">
            <span style="font-weight: bold; font-size: 14px;">Overall Compliance Score</span>
            <span style="font-size: 32px; font-weight: bold; color: ${overallCompliance >= 70 ? "#10B981" : "#F44336"};">
              ${overallCompliance}%
            </span>
          </div>
          <div style="width: 100%; height: 25px; background: #ddd; border-radius: 12px; overflow: hidden;">
            <div style="width: ${overallCompliance}%; height: 100%; background: ${overallCompliance >= 70 ? "#10B981" : "#F44336"}; border-radius: 12px;"></div>
          </div>
        </div>
      </div>

      <!-- Detailed Findings -->
      <div style="margin-bottom: 30px;">
        <h2 style="margin: 0 0 25px 0; font-size: 18px; font-weight: bold; border-bottom: 3px solid #00B8D9; padding-bottom: 12px;">
          Detailed Findings by Category
        </h2>

        ${categories
            .map(
                (category: any) => `
          <div style="margin-bottom: 25px; border: 2px solid #ddd; border-left: 5px solid ${category.status?.toLowerCase() === "compliant"
                        ? "#10B981"
                        : "#F44336"
                    }; border-radius: 8px; overflow: hidden;">
            <div style="background: #f5f5f5; padding: 16px; font-weight: bold; font-size: 15px; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #ddd;">
              <span>${category.name || "Unknown Category"}</span>
              <span style="
                display: inline-block;
                padding: 6px 12px;
                border-radius: 6px;
                font-size: 12px;
                font-weight: bold;
                vertical-align: middle;
                background: ${category.status?.toLowerCase() === "compliant"
                        ? "#D4EDDA"
                        : "#F8D7DA"
                    };
                color: ${category.status?.toLowerCase() === "compliant"
                        ? "#155724"
                        : "#721C24"
                    };
              ">
                ${category.status?.toLowerCase() === "compliant"
                        ? "✓ COMPLIANT"
                        : "⚠ NON-COMPLIANT"
                    }
              </span>
            </div>
            ${(category.requirements || []).length > 0
                        ? `
            <div style="padding: 18px;">
              ${(category.requirements || [])
                                .map(
                                    (req: any, idx: number, arr: any[]) => `
                  <div style="margin-bottom: ${idx < arr.length - 1
                                            ? "25px; padding-bottom: 25px; border-bottom: 2px solid #d3d3d3;"
                                            : "0;"
                                        }">
                    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
                      <div style="font-weight: bold; font-size: 14px; color: #333;">
                        ${(req.id || "N/A").replace(/_/g, "-")}
                      </div>
                      ${req.status?.toUpperCase() === "FAIL" || req.severity
                                            ? `
                      <span style="
                        display: inline-block;
                        padding: 4px 10px;
                        border-radius: 4px;
                        font-size: 11px;
                        font-weight: bold;
                        vertical-align: middle;
                        background: ${req.status?.toUpperCase() === "FAIL"
                                                ? "#F44336"
                                                : req.severity === "CRITICAL"
                                                    ? "#FF5252"
                                                    : req.severity === "MAJOR"
                                                        ? "#FF9800"
                                                        : "#FFC107"
                                            };
                        color: #fff;
                      ">
                        ${req.status?.toUpperCase() === "FAIL"
                                                ? "FAIL"
                                                : req.severity || "INFO"
                                            }
                      </span>
                      `
                                            : ""
                                        }
                    </div>
                    <div style="font-weight: bold; margin-bottom: 12px; font-size: 13px; color: #333;">
                      ${req.name || req.requirement_name || "Unnamed"}
                    </div>
                    ${req.evidence?.found && req.evidence.found.length > 0
                                            ? `
                    <div style="margin-bottom: 12px;">
                      <div style="font-size: 12px; color: #666; font-weight: bold; margin-bottom: 6px;">EVIDENCE FOUND</div>
                      <div style="font-size: 12px; color: #555; padding: 8px; background: #f9f9f9; border-radius: 4px;">
                        ${Array.isArray(req.evidence.found)
                                                    ? req.evidence.found.join(", ")
                                                    : req.evidence.found
                                                }
                      </div>
                    </div>
                    `
                                            : ""
                                        }
                    ${req.missing_elements && req.missing_elements.length > 0
                                            ? `
                    <div style="margin-bottom: 12px;">
                      <div style="font-size: 12px; color: #666; font-weight: bold; margin-bottom: 6px;">MISSING ELEMENTS</div>
                      <div style="font-size: 12px; color: #555;">
                        ${req.missing_elements
                                                    .map(
                                                        (el: string) =>
                                                            `<div style="margin-bottom: 4px;">• ${el}</div>`
                                                    )
                                                    .join("")}
                      </div>
                    </div>
                    `
                                            : ""
                                        }
                    ${showMedicalAuditAIRecommendations && req.ai_recommendation
                                            ? `
                    <div style="margin-bottom: 0;">
                      <div style="font-size: 12px; color: #666; font-weight: bold; margin-bottom: 6px;">RECOMMENDATION</div>
                      <div style="font-size: 12px; color: #555; padding: 8px; background: #f0f8ff; border-left: 3px solid #00B8D9; border-radius: 4px;">
                        ${req.ai_recommendation}
                      </div>
                    </div>
                    `
                                            : ""
                                        }
                  </div>
                `
                                )
                                .join("")}
            </div>
            `
                        : ``
                    }
          </div>
        `
            )
            .join("")}
      </div>

      <!-- Metadata Footer -->
      <div style="border-top: 2px solid #ddd; padding-top: 15px; font-size: 12px; color: #666;">
        <p style="margin: 0;">
          <strong>V3 Medical Note Audit Report</strong> | Generated on ${timestamp}
        </p>
        <p style="margin: 8px 0 0 0; line-height: 1.5;">
          ⚕️ <strong>Patient Confidentiality Notice:</strong> This document contains protected health information (PHI) and must be handled in accordance with applicable HIPAA regulations.
        </p>
        <p style="margin: 8px 0 0 0; line-height: 1.5;">
          ⚠️ <strong>Beta Disclaimer:</strong> This tool is currently in beta. Results are generated using AI-assisted analysis and may contain inaccuracies. Always verify findings with clinical judgment and original documentation before making clinical decisions.
        </p>
      </div>
    </div>
  `;
};
